// frontend/src/components/Pipeline/OpportunityTimeline.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
  EnvelopeIcon, PhoneIcon, CalendarIcon, DocumentTextIcon, CheckCircleIcon,
  ChatBubbleLeftRightIcon, ArrowTrendingUpIcon, ChevronDownIcon, ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { getOpportunityTimeline, getOpportunityMeetingsRag } from '../../services/api';
import SessionCard from '../SDR/SessionCard';

const DAY = 86400000;

const daysBetween = (a, b) => Math.floor((new Date(a) - new Date(b)) / DAY);

const relativeTime = (date) => {
  if (!date) return 'sem data';
  const d = Math.floor((Date.now() - new Date(date)) / DAY);
  if (d <= 0) return 'hoje';
  if (d === 1) return 'ontem';
  if (d < 30) return `há ${d} dias`;
  if (d < 365) return `há ${Math.floor(d / 30)} ${Math.floor(d / 30) === 1 ? 'mês' : 'meses'}`;
  return `há ${Math.floor(d / 365)} ano(s)`;
};

const exactDate = (date) =>
  date ? new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const KIND_ICON = {
  stage_change: ArrowTrendingUpIcon, sdr_session: ChatBubbleLeftRightIcon, meeting: CalendarIcon,
};

const ACTIVITY_ICON_BY_TITLE = {
  'E-mail': EnvelopeIcon, 'Ligação': PhoneIcon, 'Reunião': CalendarIcon, 'Tarefa': CheckCircleIcon,
};
const iconFor = (ev) => {
  if (ev.kind === 'activity') return ACTIVITY_ICON_BY_TITLE[ev.title] || DocumentTextIcon;
  return KIND_ICON[ev.kind] || DocumentTextIcon;
};

const sourceBadge = (ev) => {
  if (ev.source === 'rag') return { text: 'vía RAG · pode não ser exato', cls: 'bg-amber-100 text-amber-800' };
  if (ev.source === 'sdr') return { text: 'SDR', cls: 'bg-slate-100 text-slate-700' };
  return null;
};

const GapDivider = ({ days }) => (
  <div className="flex items-center gap-2 py-2 pl-10 text-xs text-gray-400">
    <span>⏳ {days} dias sem contato</span>
  </div>
);

const EventCard = ({ ev }) => {
  const [open, setOpen] = useState(false);
  const Icon = iconFor(ev);
  const badge = sourceBadge(ev);
  const expandable = ev.kind === 'sdr_session' || (ev.body && ev.body.length > 220);
  return (
    <div className="relative pl-10 pb-5">
      <span className="absolute left-2 top-1 h-5 w-5 rounded-full bg-white border-2 border-blue-400 flex items-center justify-center">
        <Icon className="h-3 w-3 text-blue-600" />
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-gray-900 text-sm">{ev.title}</span>
        {badge && <span className={`text-xs px-1.5 py-0.5 rounded ${badge.cls}`}>{badge.text}</span>}
        {ev.meta?.qualification_level && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-green-50 text-green-700">{ev.meta.qualification_level}{ev.meta.qualification_score != null ? ` ${ev.meta.qualification_score}/15` : ''}</span>
        )}
      </div>
      <div className="text-xs text-gray-400">{relativeTime(ev.date)} · {exactDate(ev.date)}{ev.actor ? ` · ${ev.actor}` : ''}</div>
      {ev.body && !expandable && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{ev.body}</p>}
      {ev.body && expandable && ev.kind !== 'sdr_session' && (
        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{open ? ev.body : `${ev.body.slice(0, 220)}…`}</p>
      )}
      {expandable && (
        <button onClick={() => setOpen((o) => !o)} className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:underline">
          {open ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />}
          {open ? 'ocultar' : 'ver detalhes'}
        </button>
      )}
      {open && ev.kind === 'sdr_session' && ev.meta?.session && (
        <div className="mt-2"><SessionCard s={ev.meta.session} /></div>
      )}
      {ev.source === 'rag' && ev.meta?.url && (
        <a href={ev.meta.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">abrir ata</a>
      )}
    </div>
  );
};

const OpportunityTimeline = ({ opportunityId }) => {
  const { data: local = [], isLoading, isError } = useQuery(
    ['opp-timeline', opportunityId],
    () => getOpportunityTimeline(opportunityId),
    { enabled: !!opportunityId },
  );
  const [rag, setRag] = useState({ events: [], summary: null, ragAvailable: null, loading: true });

  useEffect(() => {
    let active = true;
    setRag((r) => ({ ...r, loading: true }));
    getOpportunityMeetingsRag(opportunityId)
      .then((res) => { if (active) setRag({ events: res.events || [], summary: res.summary || null, ragAvailable: res.ragAvailable, loading: false }); })
      .catch(() => { if (active) setRag({ events: [], summary: null, ragAvailable: false, loading: false }); });
    return () => { active = false; };
  }, [opportunityId]);

  const events = useMemo(() => {
    const merged = [...local, ...rag.events];
    merged.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    return merged;
  }, [local, rag.events]);

  const lastDays = events.length && events[0].date ? daysBetween(Date.now(), events[0].date) : null;
  const staleCls = lastDays == null ? '' : lastDays > 14 ? 'text-red-600' : lastDays > 7 ? 'text-amber-600' : 'text-gray-500';

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Histórico da negociação</h2>
        {lastDays != null && (
          <span className={`text-xs font-medium ${staleCls}`}>Última interação {lastDays <= 0 ? 'hoje' : `há ${lastDays} dias`}</span>
        )}
      </div>

      {rag.summary && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 rounded-md p-3 text-gray-700">
          <span className="font-semibold text-amber-800">Resumo das reuniões (via RAG): </span>{rag.summary}
        </div>
      )}

      {isLoading && <p className="text-sm text-gray-500">Carregando…</p>}
      {isError && <p className="text-sm text-red-600">Erro ao carregar o histórico.</p>}
      {!isLoading && !isError && events.length === 0 && <p className="text-sm text-gray-500">Nenhum evento registrado ainda.</p>}

      <div className="relative">
        {events.length > 0 && <span className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" aria-hidden />}
        {events.map((ev, i) => {
          const prev = events[i - 1];
          const gap = prev && prev.date && ev.date ? daysBetween(prev.date, ev.date) : 0;
          return (
            <React.Fragment key={ev.id}>
              {gap > 7 && <GapDivider days={gap} />}
              <EventCard ev={ev} />
            </React.Fragment>
          );
        })}
      </div>

      {rag.loading && <p className="text-xs text-gray-400 mt-2">Buscando reuniões…</p>}
      {rag.ragAvailable === false && <p className="text-xs text-gray-400 mt-2">Não foi possível buscar reuniões agora.</p>}
    </section>
  );
};

export default OpportunityTimeline;
